import classNames from "classnames";
import type { TaskGroup } from "../../lib/viewEngine";
import type { TaskRowVM } from "../TaskRow";
import { TaskRow } from "../TaskRow";
import { QuickAdd } from "../QuickAdd";
import { useDrag } from "../../hooks/useDrag";

export interface LayoutProps {
  groups: TaskGroup[];
  selectedTaskId: string | null;
  onSelect: (id: string | null) => void;
  onToggleDone: (task: TaskRowVM) => void;
  hiddenProps?: string[];
  /** Drop autorisé sur ce groupe (champ de groupage mutable) ? */
  canDrop: (groupKey: string) => boolean;
  onDropTask: (taskId: string, groupKey: string) => void;
  /** Composer inline du groupe — null = masqué. */
  composerFor: (groupKey: string) => ((title: string) => void) | null;
  composerBusy?: boolean;
}

/** Layout Liste groupée — en-têtes pastille+label+count, D&D entre groupes,
 * composer inline par groupe. */
export function ListLayout({
  groups,
  selectedTaskId,
  onSelect,
  onToggleDone,
  hiddenProps,
  canDrop,
  onDropTask,
  composerFor,
  composerBusy,
}: LayoutProps) {
  const { dragId, overKey, dragProps, dropProps } = useDrag();

  return (
    <>
      {groups.map((g) => {
        const droppable = canDrop(g.key);
        const onAdd = composerFor(g.key);
        return (
          <section
            key={g.key}
            className={classNames("list-group", {
              "drop-over": overKey === g.key,
              "drop-forbidden": dragId !== null && !droppable,
            })}
            {...dropProps(g.key, (id) => onDropTask(id, g.key), droppable)}
          >
            <div
              className="group-title"
              style={g.color ? { color: g.color } : undefined}
            >
              {g.color && (
                <span className="group-dot" style={{ background: g.color }} />
              )}
              {g.label}
              <span style={{ fontWeight: 500 }}>{g.items.length}</span>
            </div>
            {g.items.map((t) => (
              <div key={t.id} {...(t.replicated ? {} : dragProps(t.id))}>
                <TaskRow
                  task={t}
                  selected={t.id === selectedTaskId}
                  onSelect={(id) => onSelect(id === selectedTaskId ? null : id)}
                  onToggleDone={onToggleDone}
                  hiddenProps={hiddenProps}
                />
              </div>
            ))}
            {onAdd && (
              <div className="group-composer">
                <QuickAdd
                  placeholder={`Ajouter dans « ${g.label} »…`}
                  onAdd={onAdd}
                  busy={composerBusy}
                />
              </div>
            )}
          </section>
        );
      })}
    </>
  );
}
